import type { Contract, ContractReplacement, DocumentRecord, Milestone } from '../../types';
import type { ContractDocumentPreviewProps, PreviewContractRef } from './ContractDocumentPreview';
import { agreementRoot, agreementVariationNumber } from '../../utils/agreementIdentity';
import { contractTitle } from '../../utils/contractTitle';

/** A stored contract and what belongs to it, as loaded on the contract page. */
export interface ContractPreviewSource {
  contract: Contract;
  /** The project's contracts, to name the ÄTA parent and the replaced version. */
  contracts: Contract[];
  milestones: Milestone[];
  descriptions: {
    contractName: string;
    assignmentDescription: string;
    otherDescription: string;
    referToAttachments: boolean;
  };
  company: { name: string; organizationNumber?: string };
  customer: { name?: string; email?: string };
  attachments: DocumentRecord[];
  /** The contract's replacement settlement, when it is part of one. */
  replacement?: ContractReplacement;
}

const amount = (value: string | number | null | undefined) => {
  const parsed = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : 0;
};

const reference = (contract: Contract | undefined): PreviewContractRef | null =>
  contract
    ? {
        contractNumber: contract.contractNumber,
        contractVersion: contract.contractVersion,
        contractName: contractTitle(contract),
        contractType: contract.contractType,
        variationNumber: contract.variationNumber,
      }
    : null;

/** A stage variation changes the cumulative plan of its original agreement. */
export const isStageVariationContract = (contract: Contract, replacement?: ContractReplacement) =>
  !!contract.stageVariation || (!!replacement?.amendmentScope && replacement.replacementContractId === contract.id);

/**
 * The payment terms line: a refund, unchanged payments, or the ordinary terms.
 * Same rule as the contract page's preview (`previewPaymentTermsKey`).
 */
export function paymentTermsKeyFor(contract: Contract, replacement?: ContractReplacement) {
  const netChangeOre = isStageVariationContract(contract, replacement)
    ? replacement?.afterRotDeltaOre ?? contract.stageVariation?.afterRotDeltaOre
    : contract.settlementType === 'price_adjustment'
      ? Math.round(amount(contract.afterRotAmount) * 100)
      : undefined;
  if (netChangeOre !== undefined && netChangeOre < 0) return 'contracts.preview.refundTerms';
  if (netChangeOre === 0) return 'contracts.preview.unchangedPaymentTerms';
  return 'contracts.preview.paymentTerms';
}

/**
 * Props for `ContractDocumentPreview` from a stored contract, so the review and
 * signing views show the same document as the wizard. Parts that are their own
 * components on the contract page (the conflict ÄTA notice, the read-only stage
 * variation) are passed by the caller as `notice` and `pricingOverride`.
 */
export function previewFromContract(source: ContractPreviewSource): ContractDocumentPreviewProps {
  const { contract, contracts, milestones, descriptions, company, customer, attachments, replacement } = source;
  const gross = amount(contract.contractAmount);
  const rot = amount(contract.rotAmount);
  const afterRot = contract.afterRotAmount != null && contract.afterRotAmount !== ''
    ? amount(contract.afterRotAmount)
    : gross - rot;
  const hasRot = rot !== 0;
  const priceAdjustment = contract.settlementType === 'price_adjustment';
  const stageVariation = isStageVariationContract(contract, replacement);
  const root = agreementRoot(contract, contracts);
  const previous = contracts.find(item => item.id === contract.stageVariation?.originalContractId || item.id === contract.replacesContractId);
  const signingStarted =
    contract.companySigned || contract.personSigned || !!contract.companySignedAt || !!contract.personSignedAt;

  return {
    contractNumber: contract.contractNumber,
    contractVersion: contract.contractVersion,
    contractName: descriptions.contractName.trim(),
    companyName: company.name,
    organizationNumber: company.organizationNumber,
    customerName: customer.name,
    customerEmail: customer.email,
    assignmentHtml: descriptions.assignmentDescription,
    referToAttachments: descriptions.referToAttachments,
    otherHtml: descriptions.otherDescription,
    gross,
    rot,
    afterRot,
    milestones: [...milestones]
      .sort((left, right) => left.milestoneNumber - right.milestoneNumber)
      .map(milestone => ({
        number: milestone.milestoneNumber,
        description: milestone.description?.trim() ?? '',
        // Without ROT the stage amount is the whole amount (same as the contract page).
        afterRotAmount: hasRot ? milestone.afterRotAmount : milestone.amount,
        endDate: milestone.endDate ? milestone.endDate.slice(0, 10) : undefined,
        kind: milestone.itemType === 'variation_price_adjustment' ? 'price_adjustment' as const : 'milestone' as const,
      })),
    attachments,
    signedAt: signingStarted ? { company: contract.companySignedAt, customer: contract.personSignedAt } : undefined,
    contractType: stageVariation || contract.contractType === 'variation' ? 'variation' : 'fixed',
    variationNumber: stageVariation ? (contract.contractVersion ?? 2) - 1 : contract.variationNumber,
    amendmentContract: stageVariation ? reference(root.id !== contract.id ? root : undefined) : null,
    previousVariationNumber: stageVariation && previous ? agreementVariationNumber(previous) : null,
    parentContract: contract.contractType === 'variation' && contract.parentContractId
      ? reference(contracts.find(item => item.id === contract.parentContractId))
      : null,
    replaces: !stageVariation && contract.replacesContractId
      ? reference(contracts.find(item => item.id === contract.replacesContractId))
      : null,
    scheduleKind: priceAdjustment ? (afterRot === 0 ? 'change' : 'price_change') : 'plan',
    paymentTermsKey: paymentTermsKeyFor(contract, replacement),
  };
}
